import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ImpactStories = ({ stories }) => {
  const [selectedStory, setSelectedStory] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleStoryClick = (story, index) => {
    setSelectedStory(story);
    setCurrentIndex(index);
  };

  const handleNext = () => {
    const nextIndex = (currentIndex + 1) % stories?.length;
    setCurrentIndex(nextIndex);
    setSelectedStory(stories?.[nextIndex]);
  };

  const handlePrev = () => {
    const prevIndex = currentIndex === 0 ? stories?.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    setSelectedStory(stories?.[prevIndex]);
  };

  const closeModal = () => {
    setSelectedStory(null);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="Users" size={24} className="text-primary" />
          <h2 className="text-xl font-semibold text-card-foreground">Impact Stories</h2>
        </div>
        <div className="text-sm text-muted-foreground">
          {stories?.length} stories
        </div>
      </div>
      {/* Stories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stories?.map((story, index) => (
          <div
            key={story?.id}
            onClick={() => handleStoryClick(story, index)}
            className="group cursor-pointer bg-background border border-border rounded-lg overflow-hidden hover:shadow-elevation-2 transition-gentle hover-scale"
          >
            <div className="relative h-48 overflow-hidden">
              <Image
                src={story?.image}
                alt={story?.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-gentle"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 right-3">
                <h3 className="text-white font-semibold text-sm line-clamp-2">
                  {story?.title}
                </h3>
              </div>
            </div>
            
            <div className="p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="MapPin" size={14} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{story?.location}</span>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-3">
                {story?.excerpt}
              </p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-primary font-medium">Read More</span>
                <Icon name="ArrowRight" size={14} className="text-primary" />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Modal */}
      {selectedStory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-card-foreground">Impact Story</h3>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlePrev}
                  disabled={stories?.length <= 1}
                >
                  <Icon name="ChevronLeft" size={16} />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {currentIndex + 1} of {stories?.length}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleNext}
                  disabled={stories?.length <= 1}
                >
                  <Icon name="ChevronRight" size={16} />
                </Button>
                <Button variant="ghost" size="sm" onClick={closeModal}>
                  <Icon name="X" size={16} />
                </Button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="relative h-64 md:h-80 rounded-lg overflow-hidden mb-6">
                <Image
                  src={selectedStory?.image}
                  alt={selectedStory?.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="space-y-4">
                <div>
                  <h2 className="text-2xl font-bold text-card-foreground mb-2">
                    {selectedStory?.title}
                  </h2>
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Icon name="MapPin" size={14} />
                      <span>{selectedStory?.location}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Calendar" size={14} />
                      <span>{selectedStory?.date}</span>
                    </div>
                  </div>
                </div>

                <div className="prose prose-sm max-w-none">
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                    {selectedStory?.fullStory}
                  </p>
                </div>

                {selectedStory?.impact && (
                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                    <h4 className="font-semibold text-card-foreground mb-2">Impact Achieved</h4>
                    <p className="text-sm text-muted-foreground">{selectedStory?.impact}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImpactStories;